/**
 * Swatch colours for the `color` listing field.
 *
 * Console stores enum options as { option, label } with no colour information,
 * so the visual is mapped here at display time. Option values are immutable
 * once created in Console — if one is misspelled, add the misspelling as the
 * key rather than renaming it there.
 *
 * Keys must match the option values in the hosted listing-fields asset.
 */
const SWATCHES = {
  black: '#111111',
  white: '#FFFFFF',
  // Note: the Console option is spelled 'vory-cream' (missing leading 'i').
  'vory-cream': '#FBF3DB',
  'ivory-cream': '#FBF3DB',
  ivory: '#FBF3DB',
  cream: '#FBF3DB',
  red: '#C81E2B',
  pink: '#E8639E',
  blush: '#F0C6C0',
  blue: '#2E63C4',
  navy: '#1B2A47',
  green: '#2F6B4F',
  yellow: '#F2CE4B',
  orange: '#E2762A',
  purple: '#7A3E9D',
  brown: '#6B4327',
  'beige-neutral': '#D6C4A8',
  beige: '#D6C4A8',
  silver: '#C4C6C8',
  'gold-metallic': '#C6A02E',
  gold: '#C6A02E',
};

// Rendered as a gradient rather than a flat fill.
const GRADIENTS = {
  'multicolor-print':
    'conic-gradient(#C81E2B, #E2762A, #F2CE4B, #2F6B4F, #2E63C4, #7A3E9D, #C81E2B)',
  multicolor:
    'conic-gradient(#C81E2B, #E2762A, #F2CE4B, #2F6B4F, #2E63C4, #7A3E9D, #C81E2B)',
  'multi-color':
    'conic-gradient(#C81E2B, #E2762A, #F2CE4B, #2F6B4F, #2E63C4, #7A3E9D, #C81E2B)',
};

// Pale swatches need a visible edge or they vanish against a white ground.
const NEEDS_OUTLINE = new Set([
  'white',
  'vory-cream',
  'ivory-cream',
  'ivory',
  'cream',
  'blush',
  'beige-neutral',
  'beige',
  'silver',
]);

/**
 * Returns inline style for a colour swatch, or null when the option has no
 * mapping (in which case no swatch should be rendered at all).
 *
 * @param {string} option enum option value from the hosted config
 * @returns {Object|null} style object for the swatch element
 */
export const getColorSwatchStyle = option => {
  if (!option || typeof option !== 'string') {
    return null;
  }
  const normalized = option.toLowerCase().trim().replace(/\s+/g, '-');
  const gradient = GRADIENTS[normalized] || GRADIENTS[option];
  if (gradient) {
    return { backgroundImage: gradient };
  }
  const color = SWATCHES[normalized] || SWATCHES[option];
  if (!color) {
    return null;
  }
  return NEEDS_OUTLINE.has(normalized) || NEEDS_OUTLINE.has(option)
    ? { backgroundColor: color, boxShadow: 'inset 0 0 0 1px #d0d0d0' }
    : { backgroundColor: color };
};

/**
 * True when a filter's values should render with colour swatches.
 *
 * Note: filter components receive the bare field key ("color") as `name`,
 * while the URL uses the namespaced form ("pub_color"). Both are accepted so
 * either can be passed safely.
 *
 * @param {string} fieldName e.g. "color" or "pub_color"
 */
export const isColorFilter = fieldName => fieldName === 'color' || fieldName === 'pub_color';
